import { useEffect, useRef } from "react";

/**
 * Reveals `.v-reveal` elements inside the returned container as they scroll into
 * view (adds `.is-in`). Attach the ref to a wrapper; every `.v-reveal` descendant
 * is observed once. Elements already in view on mount reveal immediately, and if
 * IntersectionObserver is unavailable everything is revealed up front so content
 * is never left hidden.
 *
 *   const ref = useScrollReveal();
 *   return <div ref={ref}> ...<section className="v-reveal" />... </div>;
 */
export function useScrollReveal({ threshold = 0.14 } = {}) {
  const containerRef = useRef(null);

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return undefined;

    // Sections that depend on async data (portfolio, collections…) mount *after*
    // the first paint, so we can't just scan once — we re-scan whenever the DOM
    // under the container changes, or everything added late stays hidden.
    if (typeof IntersectionObserver === "undefined") {
      const revealAll = () =>
        root.querySelectorAll(".v-reveal").forEach((el) => el.classList.add("is-in"));
      revealAll();
      const mo = new MutationObserver(revealAll);
      mo.observe(root, { childList: true, subtree: true });
      return () => mo.disconnect();
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-in");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold }
    );

    const observeNew = () => {
      root.querySelectorAll(".v-reveal:not(.is-in)").forEach((el) => observer.observe(el));
    };
    observeNew();

    const mo = new MutationObserver(observeNew);
    mo.observe(root, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      mo.disconnect();
    };
  }, [threshold]);

  return containerRef;
}

export default useScrollReveal;
